import { useGetStatsMilestones, getGetStatsMilestonesQueryKey } from "@workspace/api-client-react";
import { useTrack } from "@/contexts/track";
import { useLanguage } from "@/contexts/language";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Award, CheckCircle2, ChevronDown, Circle } from "lucide-react";

type MilestoneItem = {
  id: string;
  progress: number;
  target: number;
  unlocked: boolean;
  badgeTier: string;
};

function tierStyles(tier: string): string {
  const t = tier.toLowerCase();
  if (t === "gold") return "border-amber-400/50 bg-amber-500/10 text-amber-200";
  if (t === "silver") return "border-slate-400/40 bg-slate-400/10 text-slate-200";
  return "border-amber-800/40 bg-amber-950/30 text-amber-100/90";
}

function labelFor(
  id: string,
  M: { items: Record<string, { title: string; desc: string } | undefined> },
): { title: string; desc: string } {
  const row = M.items[id];
  if (row) return { title: row.title, desc: row.desc };
  return { title: id, desc: "" };
}

export function MilestonesPanel() {
  const { selectedTrack } = useTrack();
  const { t } = useLanguage();
  const M = t.milestones;
  const isMobile = useIsMobile();

  const { data, isLoading, isError } = useGetStatsMilestones(
    { track: selectedTrack },
    { query: { queryKey: getGetStatsMilestonesQueryKey({ track: selectedTrack }), staleTime: 120_000 } },
  );

  const items: MilestoneItem[] = Array.isArray(data?.milestones) ? data!.milestones : [];

  const headerInner = (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <CardTitle className="text-lg font-bold tracking-tight">{M.title}</CardTitle>
        <CardDescription className="text-xs leading-relaxed max-w-2xl">{M.subtitle}</CardDescription>
      </div>
      {!isLoading && !isError && data ? (
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0 pt-1">
          {M.unlockedSummary.replace("{n}", String(data.unlockedCount)).replace("{total}", String(data.totalCount))}
        </p>
      ) : null}
    </div>
  );

  const detailBody = (
    <>
      {isError ? (
        <p className="text-sm text-destructive font-mono">{M.loadError}</p>
      ) : isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">{M.badgeStrip}</p>
            <div className="flex flex-wrap gap-2">
              {items.map((m) => (
                <div
                  key={m.id}
                  title={labelFor(m.id, M).title}
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full border text-xs font-bold transition-opacity",
                    tierStyles(m.badgeTier),
                    m.unlocked ? "opacity-100" : "opacity-35 grayscale",
                  )}
                  aria-label={labelFor(m.id, M).title}
                >
                  {m.unlocked ? <CheckCircle2 className="h-5 w-5" aria-hidden /> : <Circle className="h-5 w-5" aria-hidden />}
                </div>
              ))}
            </div>
          </div>

          <ul className="space-y-4">
            {items.map((m) => {
              const pct = m.target > 0 ? Math.min(100, Math.round((m.progress / m.target) * 100)) : 0;
              const { title, desc } = labelFor(m.id, M);
              return (
                <li key={m.id} className="space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Award className={cn("h-3.5 w-3.5 shrink-0", m.unlocked ? "text-primary" : "text-muted-foreground")} aria-hidden />
                        <span>{title}</span>
                        {m.unlocked ? (
                          <span className="text-[10px] font-mono font-normal text-primary uppercase tracking-wider">{M.done}</span>
                        ) : null}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{desc}</p>
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground tabular-nums shrink-0 pt-0.5">
                      {m.progress}/{m.target}
                    </span>
                  </div>
                  <Progress value={pct} className="h-1.5" />
                </li>
              );
            })}
          </ul>

          <p className="text-[10px] text-muted-foreground/80 font-mono leading-relaxed border-t border-border/60 pt-3">{M.footer}</p>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Sheet>
        <Card className="border-border/80 bg-gradient-to-b from-card to-card/80 overflow-hidden">
          <SheetTrigger asChild>
            <button
              type="button"
              className="w-full text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <CardHeader className="pb-3">
                {headerInner}
                <div className="mt-3 flex items-center justify-between gap-2 border-t border-border/60 pt-3">
                  <span className="text-[11px] font-mono text-muted-foreground">{M.mobileOpenHint}</span>
                  <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                </div>
              </CardHeader>
            </button>
          </SheetTrigger>
        </Card>
        <SheetContent
          side="bottom"
          className="flex max-h-[min(92dvh,100%)] flex-col gap-0 overflow-y-auto rounded-t-2xl border-t p-0 px-4 pb-6 pt-3"
        >
          <SheetHeader className="space-y-1 border-b border-border/60 pb-3 text-left">
            <SheetTitle className="text-base font-bold tracking-tight">{M.title}</SheetTitle>
            <p className="text-xs text-muted-foreground leading-snug">{M.subtitle}</p>
          </SheetHeader>
          <div className="space-y-5 pt-4">{detailBody}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Card className="border-border/80 bg-gradient-to-b from-card to-card/80">
      <CardHeader className="pb-3">{headerInner}</CardHeader>
      <CardContent className="space-y-5">{detailBody}</CardContent>
    </Card>
  );
}
